import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiShoppingCart, FiMenu, FiX, FiSearch,
  FiChevronDown, FiLogOut, FiPackage, FiSettings,
  FiArrowRight, FiGrid,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import API from '../../utils/api';
import './Navbar.css';

// ── Session ID (persisted per browser tab for search analytics) ──────
const getSessionId = () => {
  let sid = sessionStorage.getItem('ap_sid');
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('ap_sid', sid);
  }
  return sid;
};

// ── Debounce helper ───────────────────────────────────────────────────
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const Navbar = () => {
  const [scrolled, setScrolled]         = useState(false);
  const [mobileOpen, setMobileOpen]     = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);
  const [searchQuery, setSearchQuery]   = useState('');
  const [navCategories, setNavCategories] = useState([]);

  // Autocomplete state
  const [acProducts,   setAcProducts]   = useState([]);
  const [acCategories, setAcCategories] = useState([]);
  const [acLoading,    setAcLoading]    = useState(false);
  const [acOpen,       setAcOpen]       = useState(false);

  const searchRef    = useRef(null);
  const debouncedQ   = useDebounce(searchQuery, 280);

  const { user, logout } = useAuth();
  const { cartCount }    = useCart();
  const location         = useLocation();
  const navigate         = useNavigate();

  // Load nav categories
  useEffect(() => {
    API.get('/catalog/public/categories?status=active')
      .then(r => setNavCategories(r.data.categories || []))
      .catch(() => {});
  }, []);

  // Scroll shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setUserDropdown(false);
    setAcOpen(false);
    setSearchQuery('');
  }, [location.pathname]);

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setAcOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch autocomplete suggestions
  useEffect(() => {
    const q = debouncedQ.trim();
    if (q.length < 2) {
      setAcProducts([]);
      setAcCategories([]);
      setAcOpen(false);
      return;
    }
    setAcLoading(true);
    API.get(`/catalog/public/search/autocomplete?q=${encodeURIComponent(q)}`)
      .then(r => {
        setAcProducts(r.data.products   || []);
        setAcCategories(r.data.categories || []);
        setAcOpen(true);
      })
      .catch(() => {})
      .finally(() => setAcLoading(false));
  }, [debouncedQ]);

  // Log search after navigating (fire-and-forget)
  const logSearch = useCallback((query, resultCount, source = 'navbar') => {
    API.post('/catalog/public/search/log', {
      query,
      resultCount,
      source,
      sessionId: getSessionId(),
    }).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setAcOpen(false);
    setSearchQuery('');
    // Navigate — result count will be logged by ProductsPage after load
    navigate(`/products?search=${encodeURIComponent(q)}`);
    logSearch(q, -1, 'navbar'); // -1 = navigated, count unknown at this point
  };

  const handleAcProductClick = (product) => {
    setAcOpen(false);
    setSearchQuery('');
    logSearch(debouncedQ, acProducts.length + acCategories.length, 'navbar');
    navigate(`/${product.slug}`);
  };

  const handleAcCategoryClick = (cat) => {
    setAcOpen(false);
    setSearchQuery('');
    logSearch(debouncedQ, acProducts.length + acCategories.length, 'navbar');
    navigate(`/${cat.slug}`);
  };

  const isActive = (path) => location.pathname === path ? 'active' : '';
  const isActivePrefix = (path) => location.pathname.startsWith(path) ? 'active' : '';

  const hasAcResults = acProducts.length > 0 || acCategories.length > 0;

  return (
    <>
      <header className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="container navbar-inner">

          {/* Logo */}
          <Link to="/" className="navbar-logo">
            <img src="/logo.svg" alt="Angel Printers" />
          </Link>

          {/* Desktop Nav */}
          <nav className="navbar-links">
            {/* Products dropdown */}
            <div className="nav-dropdown">
              <Link to="/products" className={isActivePrefix('/products')}>
                Products <FiChevronDown size={14} />
              </Link>
              <div className="dropdown-menu">
                <Link to="/products" className="dropdown-item all">All Products</Link>
                <div className="dropdown-divider" />
                {navCategories.map(c => (
                  <Link key={c.id || c.slug} to={`/${c.slug}`} className="dropdown-item">
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/services"  className={isActive('/services')}>Our Services</Link>
            <Link to="/templates" className={isActive('/templates')}>Design Templates</Link>
            <Link to="/offers"    className={`offers-link ${isActive('/offers')}`}>Offers</Link>
          </nav>

          {/* Actions */}
          <div className="navbar-actions">

            {/* ── Search with Autocomplete ── */}
            <div className="navbar-search-inline" ref={searchRef}>
              <form onSubmit={handleSearch} className="navbar-search-form">
                <FiSearch size={15} className="nav-search-icon" />
                <input
                  type="text"
                  placeholder="Search for products..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => { if (hasAcResults) setAcOpen(true); }}
                  autoComplete="off"
                />
                <button type="submit" className="nav-search-submit" aria-label="Search">
                  <FiSearch size={15} />
                </button>
              </form>

              {/* Autocomplete Dropdown */}
              {acOpen && (
                <div className="nav-autocomplete">
                  {acLoading && (
                    <div className="ac-loading">
                      <span className="ac-spinner" /> Searching…
                    </div>
                  )}

                  {!acLoading && !hasAcResults && (
                    <div className="ac-empty">
                      No results for "<strong>{debouncedQ}</strong>"
                    </div>
                  )}

                  {!acLoading && acCategories.length > 0 && (
                    <div className="ac-section">
                      <div className="ac-section-label">Categories</div>
                      {acCategories.map(cat => (
                        <button
                          key={cat.id}
                          className="ac-item ac-category"
                          onClick={() => handleAcCategoryClick(cat)}
                        >
                          <FiGrid size={14} className="ac-cat-icon" />
                          <span>{cat.name}</span>
                          <FiArrowRight size={12} className="ac-arrow" />
                        </button>
                      ))}
                    </div>
                  )}

                  {!acLoading && acProducts.length > 0 && (
                    <div className="ac-section">
                      <div className="ac-section-label">Products</div>
                      {acProducts.map(p => (
                        <button
                          key={p.id}
                          className="ac-item ac-product"
                          onClick={() => handleAcProductClick(p)}
                        >
                          {p.mainImage
                            ? <img src={p.mainImage} alt={p.name} className="ac-product-img" />
                            : <div className="ac-product-img-placeholder"><FiPackage size={14} /></div>
                          }
                          <span className="ac-product-name">{p.name}</span>
                          {p.startingPrice && (
                            <span className="ac-product-price">
                              from ₹{parseFloat(p.startingPrice).toFixed(2)}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {!acLoading && hasAcResults && (
                    <button
                      className="ac-view-all"
                      onClick={handleSearch}
                    >
                      View all results for "<strong>{debouncedQ}</strong>"
                      <FiArrowRight size={13} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Cart */}
            <Link
              to="/cart"
              className="icon-btn cart-btn"
              aria-label={`Cart${cartCount > 0 ? ` (${cartCount})` : ''}`}
            >
              <FiShoppingCart size={20} />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </Link>

            {/* Account */}
            {user ? (
              <div className="user-menu">
                <button className="icon-btn user-btn" onClick={() => setUserDropdown(v => !v)}>
                  <span className="user-avatar">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                  <FiChevronDown size={14} />
                </button>
                {userDropdown && (
                  <div className="user-dropdown">
                    <div className="user-dropdown-header">
                      <span className="user-avatar lg">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                      <div>
                        <div className="user-name">{user.name}</div>
                        <div className="user-email">{user.email}</div>
                      </div>
                    </div>
                    <div className="user-dropdown-divider" />
                    <Link to="/dashboard" className="user-dropdown-item"><FiPackage size={15} /> My Orders</Link>
                    <Link to="/dashboard/profile" className="user-dropdown-item"><FiSettings size={15} /> Profile</Link>
                    {(user.role === 'admin' || user.role === 'staff') && (
                      <Link to="/admin" className="user-dropdown-item admin-link"><FiSettings size={15} /> Admin Panel</Link>
                    )}
                    <div className="user-dropdown-divider" />
                    <button className="user-dropdown-item logout" onClick={logout}><FiLogOut size={15} /> Logout</button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="login-register-btn" aria-label="Sign In">
                Sign In
              </Link>
            )}

            {/* Hamburger */}
            <button className="icon-btn mobile-menu-btn" onClick={() => setMobileOpen(v => !v)}>
              {mobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileOpen ? 'open' : ''}`}>
        <div className="mobile-menu-inner">
          {/* Mobile search */}
          <form
            onSubmit={(e) => { e.preventDefault(); const q = e.target.q.value.trim(); if (q) { setMobileOpen(false); navigate(`/products?search=${encodeURIComponent(q)}`); } }}
            className="mobile-search-form"
          >
            <FiSearch size={16} />
            <input name="q" type="text" placeholder="Search products…" autoComplete="off" />
            <button type="submit">Go</button>
          </form>

          <Link to="/">Home</Link>
          <Link to="/products">Products</Link>
          <Link to="/services">Custom Printing</Link>
          <Link to="/templates">Design Templates</Link>
          <Link to="/offers" className="mobile-offers">Offers</Link>
          <Link to="/cart">Cart {cartCount > 0 && `(${cartCount})`}</Link>

          {user ? (
            <>
              <Link to="/dashboard">My Orders</Link>
              <Link to="/dashboard/profile">Profile</Link>
              {(user.role === 'admin' || user.role === 'staff') && <Link to="/admin">Admin Panel</Link>}
              <button onClick={logout} className="mobile-logout">Logout</button>
            </>
          ) : (
            <Link to="/login" className="mobile-signin">Sign In / Register</Link>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar;